/**
 * useFeaturedConnector — resolve a template featuredConnectors id (e.g.
 * '/managedApis/office365', 'connectionProviders/request') to a display name
 * and icon, the same way other nodes resolve connector visuals.
 *
 * Resolution order:
 *   1. Client built-in connectors (`connectionProviders/*`) — static lookup
 *      from the curated client operation set; no network fetch. This also
 *      sidesteps `isUnfetchableConnectorId`, whose leading-slash check would
 *      otherwise send slash-less template ids to a bogus managed-API fetch.
 *   2. Everything else — `useConnectorMetadata` (managed APIs via the
 *      metadata endpoint, service providers via the runtime, agent via the
 *      bundled manifest).
 *   3. Fallback — display name derived from the id's last path segment, no
 *      icon (ConnectorIcon renders its generic plug glyph).
 *
 * Single-id hook: callers render one component instance per connector id
 * (hooks can't run in loops).
 */

import { useConnectorMetadata } from '@/hooks/useConnectionMutations';
import { getBuiltInConnectorDisplayInfo } from '@/utils/connections/builtInConnectorInfo';
import { deriveConnectorDisplayName } from '@/utils/connections/connector';

export interface FeaturedConnectorInfo {
  displayName: string;
  iconUri: string | undefined;
  isLoading: boolean;
}

export function useFeaturedConnector(connectorId: string): FeaturedConnectorInfo {
  const builtIn = getBuiltInConnectorDisplayInfo(connectorId);
  // Disabled (undefined id) when the static path already resolved — the hook
  // must still be called unconditionally to keep hook order stable.
  const { data: metadata, isLoading } = useConnectorMetadata(builtIn ? undefined : connectorId);

  if (builtIn) {
    return {
      displayName: builtIn.displayName,
      iconUri: builtIn.iconUri || undefined,
      isLoading: false,
    };
  }

  return {
    displayName: metadata?.displayName ?? metadata?.name ?? deriveConnectorDisplayName(connectorId),
    iconUri: metadata?.iconUri || undefined,
    isLoading,
  };
}
